import type { FinancePartnerRow, FinancingProduct, PriceCell } from '../../../../store/oneButtonStore';
import type { CombinedFinancingSummary } from './financingCombinedSummary';
import type { selectedQuote } from './FinancingGridCard';
import {
    isStorageFinanceMethod,
    stripStorageFromFinanceMethod,
    solarStorageRequiresBatteryInQuote,
} from './productsFinanceHubHelpers';
import { normalizeFinanceGridPartner } from './utils/finance';

export { normalizeFinanceGridPartner } from './utils/finance';

const ATOMIC_PRODUCT_ORDER = ['Solar', 'Roof', 'Battery'] as const;

function getOpportunityProductRows(opportunityDetails: any): any[] {
    const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct;
    return Array.isArray(raw) ? raw : [];
}

/** Same active semantics as ProductsFinanceHubSection / commerceGuideLogic. */
function isOpportunityProductActive(p: any): boolean {
    return p?.ActiveFlag !== false && p?.activeFlag !== false;
}

function opportunityProductTypeName(p: any): string {
    return String(p?.Product_Type_Name ?? p?.product_Type_Name ?? p?.ProductType ?? '').trim();
}

function opportunityProductPartnerDisplay(p: any): string {
    let fp = String(
        p?.Finance_Partner_Display_Name ??
            p?.FinancePartnerDisplayName ??
            p?.Finance_Partner ??
            p?.finance_Partner ??
            '',
    ).trim();
    if (fp === 'Good Leap') fp = 'GoodLeap';
    return fp;
}

function opportunityProductFinanceMethod(p: any): string {
    return String(
        p?.Finance_Method_Name ?? p?.finance_Method_Name ?? p?.Finance_Method ?? p?.Contract_Type ?? '',
    ).trim();
}

function normalizeFinanceMethodForCompare(method: string | null | undefined): string {
    return stripStorageFromFinanceMethod(String(method ?? ''))
        .replace(/\s+/g, '')
        .trim()
        .toLowerCase();
}

/** Saved partner on the opportunity product row (not the legacy Finance_Partner code field alone). */
export function opportunityProductHasAssignedFinancePartner(product: any): boolean {
    if (!product) return false;
    const partnerId = Number(product.Finance_Partner_Id ?? product.FinancePartnerId ?? 0);
    if (Number.isFinite(partnerId) && partnerId > 0) return true;
    const display = opportunityProductPartnerDisplay(product);
    return !!display && !/^select finance partner$/i.test(display);
}

export function opportunityProductPartnerMatchesQuotePartner(
    product: any,
    quotePartnerName: string | null | undefined,
): boolean {
    if (!product || !quotePartnerName) return false;
    return (
        normalizeFinanceGridPartner(opportunityProductPartnerDisplay(product)) ===
        normalizeFinanceGridPartner(String(quotePartnerName))
    );
}

export function opportunityProductMethodMatchesQuoteMethod(
    product: any,
    quoteMethod: string | null | undefined,
): boolean {
    const savedMethod = normalizeFinanceMethodForCompare(opportunityProductFinanceMethod(product));
    const quoteMethodNorm = normalizeFinanceMethodForCompare(quoteMethod);
    if (!savedMethod || !quoteMethodNorm) return true;
    return savedMethod === quoteMethodNorm;
}

/** Specific validation message when an opportunity product is not ready for quote generation. */
export function getQuoteOpportunityProductValidationMessage(
    product: any,
    quotePartnerName: string | null | undefined,
    quoteMethod: string | null | undefined,
    productLabel: string,
): string | null {
    if (!product || !isOpportunityProductActive(product)) {
        return `${productLabel} is not active. Enable it in Products and save.`;
    }
    if (!opportunityProductHasAssignedFinancePartner(product)) {
        return `${productLabel} has no finance partner saved. Open Products, assign a partner, and save.`;
    }
    if (!opportunityProductPartnerMatchesQuotePartner(product, quotePartnerName)) {
        return `${productLabel} partner (${opportunityProductPartnerDisplay(product)}) does not match the selected quote (${quotePartnerName ?? 'unknown'}).`;
    }
    const partnerKey = normalizeFinanceGridPartner(opportunityProductPartnerDisplay(product));
    if (partnerKey !== 'Cash' && !opportunityProductMethodMatchesQuoteMethod(product, quoteMethod)) {
        const savedMethod = opportunityProductFinanceMethod(product) || 'none';
        const selectedMethod = String(quoteMethod ?? '').trim() || 'none';
        return `${productLabel} method (${savedMethod}) does not match the selected pricing option (${selectedMethod}). Update Products or choose a matching cell.`;
    }
    return null;
}

export function findActiveOpportunityProductByType(
    oppProducts: any[],
    productType: 'Solar' | 'Roof' | 'Battery' | 'RR',
): any | null {
    return (
        oppProducts.find((ele) => {
            if (!isOpportunityProductActive(ele)) return false;
            const pt = opportunityProductTypeName(ele);
            if (pt === productType) return true;
            if (productType === 'Roof' && (/^roof(\s+only)?$/i.test(pt) || /^rrr$/i.test(pt))) return true;
            if (productType === 'Battery' && /^battery(\s+only)?$/i.test(pt)) return true;
            if (productType === 'RR' && /^rr$/i.test(pt)) return true;
            return false;
        }) ?? null
    );
}

function findFinancingGridRow(
    financingGrid: FinancePartnerRow[],
    partnerRowPartner: string,
): FinancePartnerRow | undefined {
    const norm = normalizeFinanceGridPartner(partnerRowPartner);
    return financingGrid.find((r) => normalizeFinanceGridPartner(r.partner) === norm);
}

function gridProductTypeForSingleColumn(col: string): 'Solar' | 'Roof' | 'Battery' | null {
    const c = (col || '').trim();
    if (c === 'Solar') return 'Solar';
    if (c === 'Roof' || c === 'Roof Only') return 'Roof';
    if (c === 'Battery' || c === 'Battery Only') return 'Battery';
    return null;
}

function buildSingleProductQuote(
    row: FinancePartnerRow,
    partnerRowPartner: string,
    gridCol: FinancingProduct,
    product: any,
    priceCell: PriceCell | null | undefined,
): selectedQuote {
    const methodFromOpp = opportunityProductFinanceMethod(product);
    let methord = priceCell?.financeMethod || methodFromOpp || '';
    // Solar-only quotes cannot use Storage methods (Battery not in this quote).
    if (gridCol === 'Solar' && isStorageFinanceMethod(methord)) {
        methord = stripStorageFromFinanceMethod(methord);
    }
    return {
        product: gridCol,
        partner: partnerRowPartner,
        methord,
        price: priceCell?.purchasePrice ?? null,
        ExcelOffer: priceCell?.ExcelOffer ?? null,
        payload: priceCell?.payload ?? null,
        term: priceCell?.term,
        apr: priceCell?.apr,
        escalator: priceCell?.escalator ?? null,
        tier: priceCell?.tier,
        Recommended: priceCell?.apiRecommended === true,
    };
}

function atomicProductTypesForColumn(col: FinancingProduct | string): Array<'Solar' | 'Roof' | 'Battery'> {
    const c = String(col);
    if (c === 'Solar') return ['Solar'];
    if (c === 'Roof' || c === 'Roof Only') return ['Roof'];
    if (c === 'Battery' || c === 'Battery Only') return ['Battery'];
    if (c === 'Solar + Roof') return ['Solar', 'Roof'];
    if (c === 'Solar + Battery') return ['Solar', 'Battery'];
    if (c === 'Battery + Roof') return ['Battery', 'Roof'];
    return [];
}

/** Atomic products a combined / AI-plan summary actually covers. */
export function atomicProductTypesFromCombinedSummary(
    summary: CombinedFinancingSummary,
): Array<'Solar' | 'Roof' | 'Battery'> {
    const seen = new Set<'Solar' | 'Roof' | 'Battery'>();
    const ordered: Array<'Solar' | 'Roof' | 'Battery'> = [];
    const add = (col: FinancingProduct | string) => {
        for (const pt of atomicProductTypesForColumn(col)) {
            if (seen.has(pt)) continue;
            seen.add(pt);
            ordered.push(pt);
        }
    };
    for (const line of summary.lines) {
        for (const pick of line.picks) add(pick.product);
        for (const tag of line.productTags) add(tag);
    }
    return ordered;
}

/**
 * Same payload shape as QuotesContractsCard "Continue" after product pick:
 * one `products[]` entry per active opportunity line (its saved partner / method).
 * Grid cell pricing is overlaid when a matching combined pick exists.
 */
export function buildSelectedQuoteFromOpportunityProductTypes(
    types: Array<'Solar' | 'Roof' | 'Battery'>,
    opportunityDetails: any,
    financingGrid?: FinancePartnerRow[],
    summary?: CombinedFinancingSummary,
): selectedQuote | null {
    const oppProducts = getOpportunityProductRows(opportunityDetails);
    if (types.length === 0 || oppProducts.length === 0) return null;

    const picks = summary?.lines.flatMap((line) => line.picks) ?? [];
    const productQuotes: selectedQuote[] = [];
    const includeBattery = types.includes('Battery');

    for (const pt of types) {
        const product = findActiveOpportunityProductByType(oppProducts, pt);
        if (!product) continue;

        const partner = opportunityProductPartnerDisplay(product);
        let method = opportunityProductFinanceMethod(product);
        // Storage methods require Battery in the quote (any partner: Lease/Loan/PPA Storage).
        if (solarStorageRequiresBatteryInQuote(pt === 'Solar', includeBattery, method)) {
            method = stripStorageFromFinanceMethod(method);
        }
        const pick = picks.find((p) => atomicProductTypesForColumn(p.product).includes(pt));
        const row = pick && financingGrid ? findFinancingGridRow(financingGrid, pick.partner) : undefined;
        const cell = (row?.prices[pt] as PriceCell | undefined) ?? pick?.cell;

        productQuotes.push({
            product: pt,
            partner,
            methord: pt === 'Battery' && partner.toLowerCase().includes('cash') ? '' : method,
            price: cell?.purchasePrice ?? null,
            ExcelOffer: cell?.ExcelOffer ?? null,
            payload: cell?.payload ?? null,
            term: cell?.term,
            apr: cell?.apr,
            escalator: cell?.escalator ?? null,
            tier: cell?.tier,
            Recommended: cell?.apiRecommended === true,
        });
    }

    const unique = dedupeProductQuotes(productQuotes);
    if (unique.length === 0) return null;
    if (unique.length === 1) return unique[0];

    return {
        product: unique.map((quote) => quote.product).join(' + '),
        products: unique,
        partner: unique[0].partner,
        methord: unique[0].methord,
        price: unique[0].price ?? null,
        ExcelOffer: unique[0].ExcelOffer ?? null,
        payload: unique[0].payload ?? null,
        term: unique[0].term,
        apr: unique[0].apr,
        escalator: unique[0].escalator ?? null,
        tier: unique[0].tier,
        Recommended: unique[0].Recommended === true,
    };
}

/** Same finance-method check as FinancingGridCard `openGenerateQuoteModel` for grid / combined picks. */
export function combinedSummaryFinanceMethodsMatchOpportunity(
    summary: CombinedFinancingSummary,
    opportunityDetails: any,
): boolean {
    return findCombinedSummaryOpportunityMismatches(summary, opportunityDetails).every(
        (issue) => issue.reason !== 'method',
    );
}

export type CombinedQuoteMatchIssue = {
    product: 'Solar' | 'Roof' | 'Battery';
    reason: 'missing' | 'partner' | 'method';
    planPartner?: string;
    savedPartner?: string;
    planMethod?: string;
    savedMethod?: string;
};

function isPlaceholderPartner(partner: string): boolean {
    const n = partner.trim().toLowerCase();
    return !n || n === 'select finance partner' || n === 'select finance';
}

function isPlaceholderMethod(method: string): boolean {
    const n = method.trim().toLowerCase();
    return !n || n === 'select method' || n === 'select finance method';
}

/**
 * Each covered product must be active on the opportunity with the same finance partner
 * (and method, when the plan specifies one) as the AI / combined plan line.
 */
export function findCombinedSummaryOpportunityMismatches(
    summary: CombinedFinancingSummary,
    opportunityDetails: any,
    types?: Array<'Solar' | 'Roof' | 'Battery'>,
): CombinedQuoteMatchIssue[] {
    const oppProducts = getOpportunityProductRows(opportunityDetails);
    const checkTypes = types?.length ? types : atomicProductTypesFromCombinedSummary(summary);
    const picks = summary.lines.flatMap((line) => line.picks);
    const issues: CombinedQuoteMatchIssue[] = [];

    for (const pt of checkTypes) {
        const opp = findActiveOpportunityProductByType(oppProducts, pt);
        const pick = picks.find((p) => atomicProductTypesForColumn(p.product).includes(pt));
        const planPartner = pick?.partner ?? '';
        const planMethod = String(pick?.cell.financeMethod ?? '').trim();
        const isCashPick = !!(pick?.isCash || pick?.partner === 'Cash');

        if (!opp) {
            issues.push({ product: pt, reason: 'missing', planPartner, planMethod });
            continue;
        }

        const savedPartner = opportunityProductPartnerDisplay(opp);
        const savedMethod = opportunityProductFinanceMethod(opp);

        if (isPlaceholderPartner(savedPartner)) {
            issues.push({
                product: pt,
                reason: 'partner',
                planPartner,
                savedPartner,
                planMethod,
                savedMethod,
            });
            continue;
        }

        if (pick && !isCashPick && normalizeFinanceGridPartner(savedPartner) !== normalizeFinanceGridPartner(planPartner)) {
            issues.push({
                product: pt,
                reason: 'partner',
                planPartner,
                savedPartner,
                planMethod,
                savedMethod,
            });
            continue;
        }

        if (isPlaceholderMethod(savedMethod) && !isCashPick) {
            issues.push({
                product: pt,
                reason: 'method',
                planPartner,
                savedPartner,
                planMethod,
                savedMethod,
            });
            continue;
        }

        if (pick && !isCashPick && planMethod && savedMethod && planMethod !== savedMethod) {
            issues.push({
                product: pt,
                reason: 'method',
                planPartner,
                savedPartner,
                planMethod,
                savedMethod,
            });
        }
    }

    return issues;
}

export function formatCombinedQuoteMatchIssueLine(issue: CombinedQuoteMatchIssue): string {
    if (issue.reason === 'missing') {
        return `${issue.product} is not an active opportunity product.`;
    }
    if (issue.reason === 'partner') {
        const saved = issue.savedPartner?.trim() || 'none';
        const plan = issue.planPartner?.trim() || 'this plan';
        return `${issue.product}: saved partner is ${saved}, plan uses ${plan}.`;
    }
    const saved = issue.savedMethod?.trim() || 'none';
    const plan = issue.planMethod?.trim() || 'this plan';
    return `${issue.product}: saved method is ${saved}, plan uses ${plan}.`;
}

export function formatCombinedQuoteMatchIssues(issues: CombinedQuoteMatchIssue[]): string {
    if (issues.length === 0) return '';
    const lines = issues.map(formatCombinedQuoteMatchIssueLine);
    return `Update product finance settings to match this plan before generating a quote. ${lines.join(' ')}`;
}

function dedupeProductQuotes(quotes: selectedQuote[]): selectedQuote[] {
    const seen = new Set<string>();
    const unique: selectedQuote[] = [];
    for (const quote of quotes) {
        const productType = String(quote.product ?? '').trim();
        if (!productType || seen.has(productType)) continue;
        seen.add(productType);
        unique.push(quote);
    }
    unique.sort(
        (a, b) =>
            ATOMIC_PRODUCT_ORDER.indexOf(a.product as (typeof ATOMIC_PRODUCT_ORDER)[number]) -
            ATOMIC_PRODUCT_ORDER.indexOf(b.product as (typeof ATOMIC_PRODUCT_ORDER)[number]),
    );
    return unique;
}

/**
 * Builds `selectedQuote` for GenerateQuoteModal from combined financing summary lines
 * (recommended picks aggregated across partners/products).
 */
export function buildSelectedQuoteFromCombinedSummary(
    summary: CombinedFinancingSummary,
    financingGrid: FinancePartnerRow[],
    opportunityDetails: any,
): selectedQuote | null {
    const types = atomicProductTypesFromCombinedSummary(summary);
    if (types.length === 0) return null;
    // Include every covered product from the opportunity (saved partner / method), same as
    // the Quotes "select products → Continue" path. Do not drop Solar/Battery when their
    // saved partner differs from a recommended grid cell.
    return buildSelectedQuoteFromOpportunityProductTypes(
        types,
        opportunityDetails,
        financingGrid,
        summary,
    );
}

/**
 * Builds the `selectedQuote` payload for GenerateQuoteModal from a financing grid row + column,
 * matching FinancingGridCard `openGenerateQuoteModel` behavior (partner checks, combo `products[]`).
 */
export function buildSelectedQuoteForGenerateFromGridCell(
    partnerRowPartner: string,
    productColumn: string,
    financingGrid: FinancePartnerRow[],
    opportunityDetails: any,
): selectedQuote | null {
    const row = findFinancingGridRow(financingGrid, partnerRowPartner);
    const oppProducts = getOpportunityProductRows(opportunityDetails);
    if (!row || oppProducts.length === 0) return null;

    const col = productColumn as FinancingProduct | string;
    const priceCellCombo = row.prices[col] as PriceCell | null | undefined;

    if (col === 'Solar' || col === 'Roof' || col === 'Battery' || col === 'Roof Only' || col === 'Battery Only') {
        const pt = gridProductTypeForSingleColumn(String(col));
        if (!pt) return null;
        const product = findActiveOpportunityProductByType(oppProducts, pt);
        if (!product) return null;
        const fp = opportunityProductPartnerDisplay(product);
        if (normalizeFinanceGridPartner(fp) !== normalizeFinanceGridPartner(partnerRowPartner)) return null;

        const gridCol =
            pt === 'Solar' ? 'Solar' : pt === 'Roof' ? 'Roof' : ('Battery' as FinancingProduct);
        const priceCell = row.prices[gridCol] as PriceCell | null | undefined;
        return buildSingleProductQuote(row, partnerRowPartner, gridCol, product, priceCell);
    }

    if (col === 'Solar + Battery' || col === 'Solar + Roof') {
        const isRoof = String(col).includes('Roof');
        const secondType = isRoof ? 'Roof' : 'Battery';
        const solar = findActiveOpportunityProductByType(oppProducts, 'Solar');
        const second = findActiveOpportunityProductByType(oppProducts, secondType);
        if (!solar || !second) return null;
        let financePartner = opportunityProductPartnerDisplay(solar);
        if (normalizeFinanceGridPartner(financePartner) !== normalizeFinanceGridPartner(partnerRowPartner)) {
            return null;
        }
        if (
            normalizeFinanceGridPartner(opportunityProductPartnerDisplay(second)) !==
            normalizeFinanceGridPartner(financePartner)
        ) {
            return null;
        }
        const comboCell = row.prices[col as FinancingProduct] as PriceCell | null | undefined;
        const data: selectedQuote = { product: String(col), products: [] };
        for (const ele of [solar, second]) {
            let financeMethod = comboCell?.financeMethod ?? opportunityProductFinanceMethod(ele);
            let ExcelOffer = comboCell?.ExcelOffer;
            if (opportunityProductTypeName(ele) === 'Battery' && comboCell?.financeMethod === 'Loan') {
                financeMethod = 'Battery Loan';
            }
            if (opportunityProductTypeName(ele) === 'Battery') {
                ExcelOffer = row.prices.Solar?.ExcelOffer;
            }
            data.products!.push({
                product: opportunityProductTypeName(ele),
                partner: opportunityProductPartnerDisplay(ele),
                methord: financeMethod || '',
                price: comboCell?.purchasePrice ?? null,
                ExcelOffer: ExcelOffer ?? null,
                payload: comboCell?.payload ?? null,
                term: comboCell?.term,
                apr: comboCell?.apr,
                Recommended: comboCell?.apiRecommended === true,
            });
        }
        return data;
    }

    if (col === 'Battery + Roof') {
        const roof = findActiveOpportunityProductByType(oppProducts, 'Roof');
        const battery = findActiveOpportunityProductByType(oppProducts, 'Battery');
        if (!roof || !battery) return null;
        let financePartner = opportunityProductPartnerDisplay(roof);
        if (normalizeFinanceGridPartner(financePartner) !== normalizeFinanceGridPartner(partnerRowPartner)) {
            return null;
        }
        const comboCell = row.prices[col as FinancingProduct] as PriceCell | null | undefined;
        const data: selectedQuote = { product: String(col), products: [] };
        for (const ele of [roof, battery]) {
            const pt = opportunityProductTypeName(ele);
            const cellForRow = row.prices[pt as FinancingProduct] as PriceCell | null | undefined;
            let financeMethod = cellForRow?.financeMethod ?? comboCell?.financeMethod ?? opportunityProductFinanceMethod(ele);
            if (pt === 'Battery' && cellForRow?.financeMethod === 'Loan') {
                financeMethod = 'Battery Loan';
            }
            data.products!.push({
                product: pt,
                partner: opportunityProductPartnerDisplay(ele),
                methord: financeMethod || '',
                price: cellForRow?.purchasePrice ?? comboCell?.purchasePrice ?? null,
                ExcelOffer: cellForRow?.ExcelOffer ?? comboCell?.ExcelOffer ?? null,
                payload: cellForRow?.payload ?? comboCell?.payload ?? null,
                term: cellForRow?.term ?? comboCell?.term,
                apr: cellForRow?.apr ?? comboCell?.apr,
                Recommended: (cellForRow?.apiRecommended ?? comboCell?.apiRecommended) === true,
            });
        }
        return data;
    }

    /* Fallback: treat as single column label (e.g. dynamic / future columns) */
    if (!priceCellCombo) return null;
    return {
        product: String(col),
        partner: partnerRowPartner,
        methord: priceCellCombo.financeMethod || '',
        price: priceCellCombo.purchasePrice ?? null,
        ExcelOffer: priceCellCombo.ExcelOffer ?? null,
        payload: priceCellCombo.payload ?? null,
        term: priceCellCombo.term,
        apr: priceCellCombo.apr,
        Recommended: priceCellCombo.apiRecommended === true,
    };
}
